import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Globe, Clock, RefreshCw, PenLine, GraduationCap, Copy, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import CsvUpload from "@/pages/admin/CsvUpload";
import ConciergeLeads from "@/pages/admin/ConciergeLeads";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(r=>setO(r.data)); }, []);
  const items = [
    { icon: Users, label: "Students",         value: o?.total_students ?? "–", panel: "moe-panel-mint" },
    { icon: Globe, label: "Countries",         value: o?.total_countries ?? "–", panel: "moe-panel-cyan" },
    { icon: Users, label: "Cases",             value: o?.total_cases ?? "–", panel: "moe-panel-yolk" },
    { icon: Clock, label: "Stale playbooks",   value: o?.stale_playbooks ?? "–", panel: "moe-panel-pink" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 moe-stagger">
      {items.map((i) => (
        <div key={i.label} className={`${i.panel} p-4 sm:p-5 moe-hover-lift`}>
          <div className="flex items-center gap-2"><i.icon size={16} strokeWidth={2}/><span className="text-[10px] sm:text-xs uppercase tracking-widest font-bold">{i.label}</span></div>
          <div className="mt-2 text-3xl sm:text-4xl font-bold tabular-nums">{i.value}</div>
        </div>
      ))}
    </div>
  );
}

function Editor({ country, onClose, onSaved }) {
  const [pb, setPb] = useState(null);
  useEffect(() => {
    api.get(`/countries/${encodeURIComponent(country)}`).then(r => setPb(r.data));
  }, [country]);
  if (!pb) return <div className="moe-shimmer h-64 mt-6"/>;

  const setField = (k, v) => setPb({ ...pb, [k]: v });
  const setList = (k, v) => setPb({ ...pb, [k]: v.split(",").map(s => s.trim()).filter(Boolean) });

  const save = async () => {
    const payload = {
      visa_type: pb.visa_type, visa_fee: pb.visa_fee, issuing_authority: pb.issuing_authority,
      documents_required: pb.documents_required, notarization_note: pb.notarization_note,
      proof_of_funds_amount: pb.proof_of_funds_amount, proof_of_funds_note: pb.proof_of_funds_note,
      accommodation_required_for_visa: pb.accommodation_required_for_visa, accommodation_note: pb.accommodation_note,
      local_accommodation_platforms: pb.local_accommodation_platforms, train_app: pb.train_app,
      first_12_days_task: pb.first_12_days_task, currency: pb.currency,
      major_cities: pb.major_cities, notable_universities: pb.notable_universities,
      electronics_stores: pb.electronics_stores, household_stores: pb.household_stores,
      source_url: pb.source_url,
    };
    try {
      await api.patch(`/countries/${encodeURIComponent(country)}`, payload);
      toast.success("Playbook updated · verified today");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const reverify = async () => {
    try {
      await api.post(`/countries/${encodeURIComponent(country)}/reverify`);
      toast.success("Marked verified today"); onSaved?.();
    } catch { toast.error("Failed"); }
  };

  const Field = ({ k, label, textarea }) => (
    <div className="moe-field">
      {textarea ? <textarea value={pb[k] || ""} onChange={(e) => setField(k, e.target.value)} rows={2} placeholder=" " className="!py-4"/> :
        <input value={pb[k] || ""} onChange={(e) => setField(k, e.target.value)} placeholder=" "/>}
      <label>{label}</label>
    </div>
  );

  return (
    <div className="mt-6 moe-card p-6 moe-fade-up">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted font-semibold">Editing</div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight">{pb.country_name}</h3>
          <div className="text-xs text-muted mt-1">Last verified {pb.last_verified_date}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid={TID.adminReverify} onClick={reverify} className="moe-btn moe-btn-ghost !py-2 !px-3 text-xs"><RefreshCw size={14}/> Mark verified</button>
          <button onClick={onClose} className="moe-btn moe-btn-ghost !py-2 !px-3 text-xs">Close</button>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-3">
        <Field k="visa_type" label="Visa type"/>
        <Field k="visa_fee" label="Visa fee"/>
        <Field k="issuing_authority" label="Issuing authority"/>
        <Field k="currency" label="Currency"/>
        <Field k="proof_of_funds_amount" label="Proof of funds amount"/>
        <Field k="train_app" label="Train app"/>

        <div className="md:col-span-2"><Field k="proof_of_funds_note" label="Funds note" textarea/></div>
        <div className="md:col-span-2"><Field k="notarization_note" label="Notarization note" textarea/></div>
        <div className="md:col-span-2"><Field k="first_12_days_task" label="First 12 days priority task" textarea/></div>
        <div className="md:col-span-2"><Field k="accommodation_note" label="Accommodation note" textarea/></div>
        <div className="md:col-span-2 flex items-center gap-2">
          <input type="checkbox" checked={!!pb.accommodation_required_for_visa} onChange={(e)=>setField("accommodation_required_for_visa", e.target.checked)}/>
          <label className="text-sm">Accommodation required for the visa application itself</label>
        </div>

        <div className="moe-field md:col-span-2">
          <input value={(pb.documents_required || []).join(", ")} onChange={(e) => setList("documents_required", e.target.value)} placeholder=" "/>
          <label>Documents required (csv)</label>
        </div>
        {["major_cities","notable_universities","local_accommodation_platforms","electronics_stores","household_stores"].map(k => (
          <div key={k} className="moe-field">
            <input value={(pb[k] || []).join(", ")} onChange={(e) => setList(k, e.target.value)} placeholder=" "/>
            <label>{k.replace(/_/g," ")} (csv)</label>
          </div>
        ))}
        <div className="moe-field md:col-span-2">
          <input value={pb.source_url || ""} onChange={(e) => setField("source_url", e.target.value)} placeholder=" "/>
          <label>Source URL</label>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button data-testid={TID.adminSave} onClick={save} className="moe-btn moe-btn-primary">Save changes</button>
      </div>
    </div>
  );
}


function ManifestAdmin() {
  const [ov, setOv] = useState(null);
  const [reports, setReports] = useState([]);
  const [openReport, setOpenReport] = useState(null); // {report}
  const reload = () => {
    api.get("/manifest/admin/overview").then(r => setOv(r.data));
    api.get("/manifest/admin/reports").then(r => setReports(r.data.reports || []));
  };
  useEffect(() => { reload(); }, []);
  if (!ov) return <div className="moe-shimmer h-40 mt-6"/>;
  const cards = [
    { label: "Opted-in students",  value: ov.opted_in_students,  panel: "moe-panel-mint" },
    { label: "Active connections", value: ov.active_connections, panel: "moe-panel-cyan" },
    { label: "Pending requests",   value: ov.pending_requests,   panel: "moe-panel-yolk" },
    { label: "Open reports",       value: ov.open_reports,       panel: "moe-panel-pink" },
  ];
  return (
    <div className="mt-6" data-testid="admin-manifest">
      <div className="grid md:grid-cols-4 gap-4 moe-stagger">
        {cards.map(c => (
          <div key={c.label} className={`${c.panel} p-5 moe-hover-lift`}>
            <div className="text-xs uppercase tracking-widest font-bold">{c.label}</div>
            <div className="mt-2 text-4xl font-bold tabular-nums">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 moe-card p-6">
        <div className="text-xs uppercase tracking-widest text-elec font-bold">Reports queue</div>
        <div className="mt-1 text-[12px] text-muted leading-snug max-w-2xl">
          Admins can only view a conversation or Moment thread <b>after a user has filed a report on it</b>. Every view is written to the audit log with your admin identity.
        </div>
        <div className="mt-2 text-sm text-muted">Total messages exchanged: {ov.total_messages} · conversations: {ov.total_conversations}</div>
        {reports.length === 0 ? (
          <div className="mt-6 text-sm text-muted italic" data-testid="admin-reports-empty">No reports open.</div>
        ) : (
          <div className="mt-4 divide-y divide-ink/10" data-testid="admin-reports-list">
            {reports.map(r => (
              <div key={r.id} className="py-3 flex items-start justify-between gap-4" data-testid={`report-${r.id}`}>
                <div className="min-w-0 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold uppercase tracking-widest text-[color:var(--moe-pink)]">{r.reason}</span>
                    <span className="text-[10px] uppercase tracking-widest text-muted font-bold px-1.5 py-0.5 rounded border border-ink/20">
                      {r.source_kind || (r.moment_id ? "moment" : "conversation")}
                    </span>
                  </div>
                  <div className="mt-1 text-ink">{r.details || <span className="italic text-muted">No details provided.</span>}</div>
                  <div className="text-muted mt-1">
                    {r.created_at?.slice(0,19)} · reporter {r.reporter_case_id?.slice(0,8)} → reported {r.reported_case_id?.slice(0,8)}
                    {r.moment_id && <> · moment {r.moment_id.slice(0,8)}</>}
                    {r.conversation_id && <> · convo {r.conversation_id.slice(0,8)}</>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border border-ink/25">{r.status}</span>
                  <button
                    data-testid={`report-view-${r.id}`}
                    onClick={() => setOpenReport(r)}
                    className="moe-btn moe-btn-ghost !py-1 !px-2 text-[10px] uppercase tracking-widest">
                    View reported thread
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {openReport && (
        <ReportedThreadModal report={openReport} onClose={() => setOpenReport(null)}/>
      )}
    </div>
  );
}

function ReportedThreadModal({ report, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  useEffect(() => {
    api.get(`/admin/reports/${report.id}/conversation`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load reported thread."));
  }, [report.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="admin-reported-thread-modal">
      <div className="bg-paper max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-2xl">
        <div className="sticky top-0 bg-paper border-b border-ink/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Reported thread</div>
            <div className="text-base font-bold">
              {(data?.kind || (report.moment_id ? "moment" : "conversation")).toUpperCase()} · {report.reason}
            </div>
            <div className="text-[11px] text-muted mt-0.5">
              Your view of this thread is audit-logged.
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="admin-reported-thread-close">
            <X size={18}/>
          </button>
        </div>
        <div className="p-6">
          {err && <div className="text-sm text-[color:var(--moe-pink)]">{err}</div>}
          {!data && !err && <div className="moe-shimmer h-32"/>}
          {data && data.kind === "conversation" && (
            <div data-testid="admin-reported-messages">
              {(data.messages || []).length === 0 ? (
                <div className="text-sm text-muted italic">{data.note || "No messages in this conversation."}</div>
              ) : (
                <div className="space-y-2">
                  {data.messages.map((m, i) => (
                    <div key={m.id || i} className="rounded-lg border border-ink/10 p-3 text-[13px]">
                      <div className="text-[10px] uppercase tracking-widest text-muted font-bold">
                        {m.case_id?.slice(0, 8)} · {m.sent_at?.slice(0, 19)}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{m.text || m.body || ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {data && data.kind === "moment" && (
            <div data-testid="admin-reported-moment-comments">
              {data.moment && (
                <div className="mb-4 moe-card p-3 text-[12px]">
                  <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Moment</div>
                  <div className="mt-1 font-bold">{data.moment.title || "Untitled"}</div>
                  <div className="text-muted">{data.moment.kind} · {data.moment.status}</div>
                </div>
              )}
              {(data.comments || []).length === 0 ? (
                <div className="text-sm text-muted italic">No comments in this thread.</div>
              ) : (
                <div className="space-y-2">
                  {data.comments.map(c => (
                    <div key={c.id}
                         className={`rounded-lg border p-3 text-[13px] ${c.id === data.flagged_comment_id ? "border-[color:var(--moe-pink)] bg-[color:var(--moe-pink)]/5" : "border-ink/10"}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted font-bold flex items-center gap-2">
                        {c.case_id?.slice(0, 8)} · {c.created_at?.slice(0, 19)}
                        {c.id === data.flagged_comment_id && <span className="text-[color:var(--moe-pink)]">FLAGGED</span>}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{c.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Duplicates() {  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setData(null);
    const r = await api.get("/admin/duplicates");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const del = async (pid) => {
    if (!window.confirm("Delete this partnership row? This cannot be undone.")) return;
    setBusy(pid);
    try {
      await api.delete(`/admin/partnerships/${pid}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    setBusy(null);
  };

  const keep = async (pid) => {
    setBusy(pid);
    try {
      await api.post(`/admin/partnerships/${pid}/keep`);
      toast.success("Kept. Flag cleared");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    setBusy(null);
  };

  if (!data) return <div className="moe-shimmer h-40 mt-6"/>;

  const empty = data.detected_group_count === 0 && data.flagged_count === 0;
  if (empty) {
    return (
      <div className="mt-6 moe-card p-10 text-center">
        <Check size={28} className="mx-auto text-elec" strokeWidth={2}/>
        <div className="mt-3 text-lg font-bold">All clean.</div>
        <div className="text-sm text-muted mt-1">No duplicate rows and no partnerships flagged for review.</div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-8">
      <div className="text-sm text-muted">
        <span className="font-bold text-ink">{data.detected_group_count}</span> duplicate group{data.detected_group_count === 1 ? "" : "s"} detected ·
        <span className="font-bold text-ink"> {data.flagged_count}</span> row{data.flagged_count === 1 ? "" : "s"} flagged in source CSV.
      </div>

      {data.detected.map((g, idx) => (
        <div key={idx} data-testid={`dup-group-${idx}`} className="moe-card p-5">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted font-bold flex items-center gap-1.5">
                <Copy size={12}/> Same (home, partner, country)
              </div>
              <div className="mt-1 text-lg font-bold">
                {g.key.home} → {g.key.partner}
                <span className="text-muted font-normal"> · {g.key.country}</span>
              </div>
            </div>
            <span className="moe-chip moe-chip-pink self-start">{g.count}× rows</span>
          </div>

          <div className="mt-4 divide-y divide-ink/10 border border-ink/10 rounded-md overflow-hidden">
            {g.rows.map((r) => (
              <div key={r.id} data-testid={`dup-row-${r.id}`} className="p-3 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 text-xs">
                  <div className="font-mono text-[10px] text-muted">{r.id}</div>
                  <div className="mt-1"><span className="text-muted">city</span> {r.city || "–"} · <span className="text-muted">programme</span> {r.programme_type || "–"}</div>
                  <div><span className="text-muted">confidence</span> {r.data_confidence || "–"} · <span className="text-muted">flag</span> {r.duplicate_check || <em className="text-muted/60">clear</em>}</div>
                  <div className="text-[10px] text-muted mt-1">source: {r.source} · created {r.created_at?.slice(0,10)}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`dup-keep-${r.id}`}
                    disabled={busy === r.id || !r.duplicate_check}
                    onClick={() => keep(r.id)}
                    className="moe-btn moe-btn-ghost !py-1.5 !px-2.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    title={r.duplicate_check ? "Clear the review flag" : "Already cleared"}>
                    <Check size={12}/> Keep
                  </button>
                  <button
                    data-testid={`dup-delete-${r.id}`}
                    disabled={busy === r.id}
                    onClick={() => del(r.id)}
                    className="moe-btn moe-btn-ghost !py-1.5 !px-2.5 text-xs text-red-600 hover:bg-red-50">
                    <Trash2 size={12}/> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Flagged-but-not-a-duplicate-group (rows flagged in CSV yet only one exists) */}
      {(() => {
        const detectedIds = new Set(data.detected.flatMap((g) => g.rows.map((r) => r.id)));
        const orphanFlagged = data.flagged.filter((r) => !detectedIds.has(r.id));
        if (orphanFlagged.length === 0) return null;
        return (
          <div className="moe-card p-5">
            <div className="text-xs uppercase tracking-widest text-muted font-bold">Flagged by source, no live duplicate</div>
            <div className="mt-1 text-sm text-muted">These rows were tagged “Duplicate - review” in the CSV but appear only once in the DB. Clear the flag if you want to keep them.</div>
            <div className="mt-4 divide-y divide-ink/10 border border-ink/10 rounded-md overflow-hidden">
              {orphanFlagged.map((r) => (
                <div key={r.id} className="p-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-xs min-w-0">
                    <div className="font-bold">{r.home_institution_short} → {r.exchange_partner}</div>
                    <div className="text-muted">{r.city} · {r.country}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => keep(r.id)} className="moe-btn moe-btn-ghost !py-1.5 !px-2.5 text-xs"><Check size={12}/> Keep</button>
                    <button onClick={() => del(r.id)} className="moe-btn moe-btn-ghost !py-1.5 !px-2.5 text-xs text-red-600 hover:bg-red-50"><Trash2 size={12}/> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function Admin() {
  const [countries, setCountries] = useState([]);
  const [cases, setCases] = useState([]);
  const [bschools, setBschools] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("analytics");

  const load = async () => {
    const [c, k, b] = await Promise.all([api.get("/countries"), api.get("/admin/cases"), api.get("/bschools")]);
    setCountries(c.data.countries); setCases(k.data.cases); setBschools(b.data.bschools);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24">
      <div className="text-xs uppercase tracking-widest text-muted font-semibold">Admin</div>
      <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Registry</h1>

      <div className="mt-8"><Overview/></div>

      <div className="mt-10 flex gap-2 border-b border-ink/20 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {[
          { k: "analytics", label: "Analytics" },
          { k: "users",     label: "Users" },
          { k: "playbooks", label: "Playbooks" },
          { k: "duplicates", label: "Duplicates" },
          { k: "cases", label: "Cases" },
          { k: "schools", label: "B-Schools" },
          { k: "manifest", label: "Manifest" },
          { k: "leads", label: "Leads" },
          { k: "csv", label: "CSV upload" },
        ].map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-3 sm:px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab===t.k ? "border-elec text-elec" : "border-transparent text-muted"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "analytics" ? (
        <AdminAnalytics/>
      ) : tab === "users" ? (
        <AdminUsers/>
      ) : tab === "playbooks" ? (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3 moe-stagger">
          {countries.map((c) => (
            <div key={c.country_name} className="moe-card p-4 moe-hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold text-ink">{c.country_name}</div>
                  <div className="text-xs text-muted">Verified {c.last_verified_date}</div>
                </div>
                {c.needs_reverification && <span className="moe-chip moe-chip-yolk moe-pulse"><Clock size={11}/> Re-check</span>}
              </div>
              <button data-testid={TID.adminEdit(c.country_name)} onClick={() => setEditing(c.country_name)} className="mt-3 moe-btn moe-btn-ghost !py-1.5 !px-3 text-xs">
                <PenLine size={12}/> Edit
              </button>
            </div>
          ))}
        </div>
      ) : tab === "duplicates" ? (
        <Duplicates/>
      ) : tab === "cases" ? (
        <div className="mt-6 moe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-xs uppercase tracking-widest text-muted">
              <tr>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Country</th>
                <th className="text-left p-3">Duration</th>
                <th className="text-left p-3">Start</th>
                <th className="text-left p-3">Visa</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="border-t border-ink/10">
                  <td className="p-3">{c.student?.name} <span className="text-muted">· {c.student?.email}</span></td>
                  <td className="p-3">{c.country}</td>
                  <td className="p-3">{c.duration_days}d</td>
                  <td className="p-3">{c.start_date}</td>
                  <td className="p-3 text-muted">{c.visa_path?.visa_type}</td>
                </tr>
              ))}
              {cases.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted text-sm">No cases yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : tab === "manifest" ? (
        <ManifestAdmin/>
      ) : tab === "leads" ? (
        <ConciergeLeads/>
      ) : tab === "csv" ? (
        <CsvUpload/>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3 moe-stagger">
          {bschools.map(b => (
            <div key={b.short} className="moe-card p-4">
              <div className="text-xs font-bold text-elec">RANK #{b.rank}</div>
              <div className="mt-1 font-bold">{b.short}</div>
              <div className="text-xs text-muted">{b.name}</div>
              <div className="text-xs text-muted mt-1">{b.city}</div>
            </div>
          ))}
        </div>
      )}

      {editing && <Editor country={editing} onClose={() => setEditing(null)} onSaved={() => { load(); }}/>}
    </div>
  );
}
